/**
 * Qué pasa exactamente al bajar al plan gratuito.
 *
 * Lo comparten los dos sitios desde donde se puede cancelar —Facturación y la
 * comparativa de planes—, y eso es lo importante: cuando cada pantalla se
 * escribía su propio aviso, una hablaba de límites y la otra de dominios, y
 * ninguna de las dos contaba lo mismo. Una promesa sobre lo que se pierde no
 * puede depender del botón por el que se llegó.
 *
 * ── Por qué también lo que NO se pierde ─────────────────────────────────────
 * Una lista de pérdidas a secas se lee como una amenaza, y lo que consigue es
 * que quien ya decidió bajarse dude de si va a perder su sitio. No va a
 * perderlo: sigue publicado y no se borra nada. Decirlo con el mismo peso es lo
 * honesto y, de paso, es lo que evita el mensaje de soporte del día siguiente.
 *
 * Los topes salen de `SITE_PLAN_LIMITS`, que es lo mismo que aplica el
 * servidor: escribirlos a mano aquí sería una segunda tabla de límites que se
 * desincroniza en cuanto cambie un plan.
 */
import { useEffect, useState } from 'react'
import { IconAlertTriangle, IconArrowRight, IconCheck, IconX } from '@tabler/icons-react'
import { countLabel, PLAN_LABEL, SITE_PLAN_LIMITS, type LimitedResource } from '../lib/planLimits'
import { planDowngrade, type PlanDowngrade } from '../lib/planFeatures'
import { useI18n } from '../lib/i18n'
import type { Translate } from '@webbuilder/i18n'
import { getDowngradePreview, type BillingPlan, type DowngradePreview } from '../lib/api'

/**
 * En qué se convierte cada ventaja del plan que se abandona.
 *
 * Las exclusivas del plan anual sólo entran si el plan que se deja era anual:
 * ofrecer como pérdida un dominio incluido que nunca se tuvo es inventarse una.
 */
function changes(plan: BillingPlan, annual: boolean, t: Translate): PlanDowngrade[] {
  if (plan === 'free') return []
  return (planDowngrade(t)[plan] ?? []).filter((row) => annual || !row.annualOnly)
}

/**
 * Qué pasa exactamente con lo que ya se pasó del tope.
 *
 * Lo importante es lo que NO ocurre: nada se borra. Un aviso que sólo dijera
 * «excedes el límite» deja a quien lo lee imaginándose lo peor, y lo peor —que
 * le recorten los contactos o le tiren páginas— es justamente lo que no pasa.
 */
function overTooltip(resource: LimitedResource, over: { used: number; sites: string[] }, t: Translate): string {
  const limit = SITE_PLAN_LIMITS.free[resource]
  const cap = limit === null ? '' : t('plans.cancel.overCap', { limit })
  const count = countLabel(resource, over.used, t)
  /* El sitio, por su nombre, cuando es uno solo. Con varios no cabe la lista y
     el nombre deja de ayudar: lo que hay que saber es que pasa en más de uno. */
  return over.sites.length === 1
    ? t('plans.cancel.overOne', { count, cap, site: over.sites[0] ?? '' })
    : t('plans.cancel.overMany', { count, cap, sites: over.sites.length })
}

export function CancelMembershipSummary({ token, plan, until, annual = false }: {
  token: string
  /** El plan que se deja. De él sale la lista de lo que incluye. */
  plan: BillingPlan
  /** Hasta cuándo sigue vigente lo pagado, ya formateado. */
  until: string
  /** Si el plan que se deja era anual. Decide si entran sus exclusivas. */
  annual?: boolean
}) {
  const { t } = useI18n()
  const [preview, setPreview] = useState<DowngradePreview | null>(null)

  useEffect(() => {
    let alive = true
    getDowngradePreview(token)
      .then((data) => { if (alive) setPreview(data) })
      /* Callado: que no se pueda calcular no cambia lo que hace el botón, y un
         error aquí sólo añadiría ruido a una decisión ya incómoda. */
      .catch(() => {})
    return () => { alive = false }
  }, [token])

  const projects = preview?.projects ?? []

  /**
   * Qué topes excede ya esta cuenta, y por cuánto.
   *
   * Cruzar el renglón con lo que la cuenta tiene de verdad es lo que convierte
   * «10.000 contactos → 100 contactos» en «y tú tienes 340». Sin eso, cada
   * quien tiene que acordarse de sus propias cifras para saber si ese renglón
   * le afecta — y quien no se acuerda asume que no.
   */
  const exceeded = new Map<LimitedResource, { used: number; sites: string[] }>()
  for (const project of projects) {
    for (const overage of project.overages) {
      const entry = exceeded.get(overage.resource) ?? { used: 0, sites: [] }
      entry.used = Math.max(entry.used, overage.used)
      entry.sites.push(project.projectName)
      exceeded.set(overage.resource, entry)
    }
  }

  return (
    <div className="dash-cancel-summary">
      {/* Primero la cancelación como hecho, luego el acceso que se conserva.
          Es el orden que usan Netflix, Spotify, Figma o Adobe, y funciona porque
          responde en ese orden a lo que se pregunta: «¿ya quedó hecho?» antes
          que «¿desde cuándo me afecta?».

          Los dos intentos anteriores empezaban por lo segundo —«no se cancela
          hoy», «hoy no cambia nada»— y eso deja creyendo que la cancelación no
          se registró: quien lee «no cambia nada» vuelve a pulsar el botón. */}
      <div className="dash-cancel-summary-lead">
        <p>
          {t('plans.cancel.lead1', { plan: PLAN_LABEL[plan] })}
          <strong>{t('plans.cancel.leadStrong', { date: until })}</strong>
          {t('plans.cancel.lead2')}
        </p>
        <p>{t('plans.cancel.keepUntil', { plan: PLAN_LABEL[plan] })}</p>
        <p>{t('plans.cancel.reactivate', { date: until })}</p>
      </div>

      <section>
        <h4>{t('plans.cancel.losing')}</h4>
        {/* Cada renglón dice en qué se convierte, no sólo que se pierde.
            «10.000 contactos → 100 contactos» es una pérdida que se puede
            medir; «10.000 contactos» tachado obliga a acordarse de cuánto trae
            el plan gratis para saber si eso duele o no.

            Lo que desaparece sin equivalente no lleva flecha a ninguna parte:
            lleva un aspa. Son dos cosas distintas y presentarlas igual hace que
            perder el dominio incluido parezca sólo un tope más bajo. */}
        <ul className="dash-cancel-summary-list">
          {changes(plan, annual, t).map((row) => (
            <li key={row.from} className={row.to ? '' : 'is-gone'}>
              <span className="dash-cancel-from">{row.from}</span>
              {row.to ? <IconArrowRight size={14} aria-hidden="true" /> : <IconX size={14} aria-hidden="true" />}
              {/* El aviso va DENTRO del bloque de la derecha, pegado a su texto,
                  y no como último elemento del renglón. Suelto al final se
                  llevaba su propio ancho, así que los renglones que lo tenían
                  encogían sus dos columnas y su texto no cuadraba con el de los
                  que no.

                  Sólo donde la cuenta ya se pasa. Un aviso en todos los
                  renglones no avisa de nada: lo útil de éste es que aparece en
                  los tres o cuatro que de verdad le tocan a quien mira. */}
              <span className="dash-cancel-to">
                {row.to ?? t('plans.cancel.gone')}
                {row.resource && exceeded.has(row.resource) ? (
                  <span
                    className="dash-cancel-over-icon"
                    data-tooltip={overTooltip(row.resource, exceeded.get(row.resource)!, t)}
                    tabIndex={0}
                    role="note"
                    aria-label={overTooltip(row.resource, exceeded.get(row.resource)!, t)}
                  >
                    <IconAlertTriangle size={14} aria-hidden="true" />
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Una frase y no una segunda columna. Lo que se conserva sigue haciendo
          falta —sin ello, una lista de pérdidas hace dudar de si se pierde el
          sitio, y no se pierde— pero enfrentarlo en dos columnas ponía las dos
          cosas al mismo nivel y convertía una decisión en una comparación. */}
      <p className="dash-cancel-summary-kept">
        <IconCheck size={13} aria-hidden="true" />
        <span>{t('plans.cancel.kept')}</span>
      </p>

      {/* Aquí iba el detalle por proyecto —«Proyecto Verde: 12 páginas de 3»—
          en su propio bloque al pie. Decía lo mismo que el aviso de cada
          renglón, pero lejos de él: había que leer «3 páginas», bajar, buscar el
          proyecto y volver. Ahora vive en el globo del triángulo, en el renglón
          al que pertenece. */}
    </div>
  )
}
